/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getApplicationRoles} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Account, EntityStatus, Role} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, Options} from '../components/menus/Dropdown';

export type RoleChooserProps = {
  accountId: number;
  appKey: string,
  roleFilterAccountIds?: number[] | null;
  selectedValue?: Role;
  onChange?: (role: Role) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  readOnly?: boolean;
  required?: boolean;
  hasError?: boolean;
  classes?: string;
  name?: string;
  defaultValue?: Account;
  useDisabledStyles?: boolean;
  currentGroup?: Account;
  defaultGroup?: Account;
}

/**
* Generates the role chooser dropdown labels and values based on
* the roles fetched for the selected account or user's default account
* and appKey.
*/
const getOptions = (roles: Role[]): Options[] => {
  return createOptions({
    items     : roles,
    valProp   : 'id',
    labelProp : 'name',
    augmentor : (item: Role, option: { value: string; label: string; }) => {
      return {...option, disabled: item.status !== EntityStatus.Enabled};
    }
  });
};

/**
 * Provides a utility which filters a list of roles for the
 * currently selected value.
 */
const getSelectedValue = (
  roles: Role[],
  selected: string
): Role => {
  return roles.find(({name}: Role) => name === selected);
};

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * @RoleChooser
 *
 * The RoleChooser component displays a list of roles
 * based on the provided account and appKey, and allows for selecting a role
 * from the list for subsequent use.
 *
 * ```typescript
 *
 *     import {RoleChooser} from '@ion/dls/src/editors/';
 *
 *     // filter the list of roles based on the given asset type and categories ...
 *     <RoleChooser onChange={setRole}
 *       accountId={selectedAccount}
 *       appKey={appKey}
 *       />
 *
 * ```
 *
 * @accountId        The id of the Account for which roles are to be loaded.
 * @appKey           The appKey
 * @selectedValue    Selected role value on Editing
 * @onChange         Callback prop which will be invoked with the seleted role.
 * @onLoadingChange  Callback prop which will be invoked whenever the loading state changes.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @hasError         When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @name             name for the component that gets passed to the root element.
 */
export const RoleChooser = ({
  accountId,
  appKey,
  selectedValue,
  onChange,
  onLoadingChange,
  defaultValue,
  readOnly,
  required,
  classes,
  hasError = false,
  useDisabledStyles = false,
  name,
  currentGroup,
  defaultGroup,
  roleFilterAccountIds
}: RoleChooserProps) => {
  const _classes                  = useStyles();
  const [roles, setRoles]         = useState<Role[]>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(accountId));
  const [selected, setSelected]   = useState<string>(selectedValue?.name || '');
  const [options, setOptions]     = useState<Options[]>([]);
  const isSameGroup: boolean      = currentGroup && currentGroup === defaultGroup;

  // manages loading roles based on the current groupId (falls back to accountId from GGC) and appKey props ...
  const loadRoles = async function(id: number, appKey: string) {
    let results: Role[] = [];

    setIsLoading(true);
    onLoadingChange?.(true);

    if (!defaultValue || !isSameGroup) {
      setSelected('');
    }
    try {
      results = await getApplicationRoles(appKey, id);
    } catch (err) {
      error(`Roles: Error occurred retrieving Roles for account ${id}`);
      if (defaultValue) {
        onLoadingChange?.(false);
      }
    }

    let res = results;
    if (roleFilterAccountIds && roleFilterAccountIds.length > 0) {
      res = results.filter(role => !roleFilterAccountIds.includes(entityId(role.account)));
    }

    setRoles(res);
    setOptions(getOptions(res));
    setIsLoading(false);
    onLoadingChange?.(false);
  };

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue.name);
    }
  }, [defaultValue]);

  // the roles are loaded ...
  useEffect(() => {
    currentGroup && appKey && loadRoles(entityId(currentGroup) ?? accountId, appKey);
  }, [currentGroup]);

  return (
    <div className={classes || _classes.root}>
      <Dropdown displaySelectedLabel
        required={required}
        disabled={readOnly || isLoading}
        useDisabledStyles={useDisabledStyles}
        hasError={hasError}
        options={options}
        loading={isLoading}
        value={!isLoading ? selected : translate('common:states.loading')}
        fieldLabel={translate('common:editors.role.label')}
        placeholder={translate('common:editors.role.placeholder')}
        preferPlaceholder={!isLoading && !selected}
        onChange={onChange ? function({target}) {
          const name = target?.value as unknown as string;
          setSelected(name);
          onChange(getSelectedValue(roles, name));
        } : null}
        name={name} />
    </div>
  );
};

export default RoleChooser;
